import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import API_BASE_URL from '../config/api';
import { trackEvent } from '../utils/analytics';

const AppleIcon = () => (
  <svg viewBox="0 0 814 1000" width="15" height="15" fill="#ffffff" aria-hidden="true">
    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.8 0 663 0 541.8c0-194.3 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg viewBox="0 0 23 23" width="15" height="15" aria-hidden="true">
    <path fill="#f35325" d="M1 1h10v10H1z"/>
    <path fill="#81bc06" d="M12 1h10v10H12z"/>
    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
    <path fill="#ffba08" d="M12 12h10v10H12z"/>
  </svg>
);

const LOGIN_OPTIONS = [
  { id: 'apple',     icon: <AppleIcon />,     label: 'Apple'            },
  { id: 'facebook',  icon: 'f',               label: 'Facebook'         },
  { id: 'twitter',   icon: '𝕏',               label: 'X (Twitter)'     },
  { id: 'github',    icon: '<>',              label: 'GitHub'            },
  { id: 'discord',   icon: '💬',              label: 'Discord'          },
  { id: 'microsoft', icon: <MicrosoftIcon />, label: 'Microsoft'        },
  { id: 'password',  icon: '✉',               label: 'Email & Password' },
];

const Login = () => {
  const { login, refreshPlan } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOtherOptions, setShowOtherOptions] = useState(false);
  const [votedOption, setVotedOption] = useState(null);

  const handleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      setError(null);

      // Decode the JWT token to get user information
      const decoded = jwtDecode(credentialResponse.credential);

      const userData = {
        email: decoded.email,
        name: decoded.name,
        picture: decoded.picture,
        sub: decoded.sub
      };

      // Save user to database
      const response = await fetch(`${API_BASE_URL}/api/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          googleId: decoded.sub,
          email: decoded.email,
          name: decoded.name,
          picture: decoded.picture
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save user to database');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to save user');
      }

      console.log('User saved to database:', result.data);

      // Add database user ID and role to userData
      const userDataWithId = {
        ...userData,
        id: result.data.id,
        role: result.data.role || null
      };

      // Save user to localStorage and update auth context
      login(userDataWithId);

      // Fetch subscription status so plan/features are correct immediately after login
      await refreshPlan(decoded.sub);

      // Track successful login
      trackEvent('Authentication', 'Login Success', userData.email);

      navigate('/');
    } catch (error) {
      console.error('Error during login:', error);
      setError('Login failed. Please try again.');

      // Track login failure
      trackEvent('Authentication', 'Login Failed', error.message);

      setLoading(false);
    }
  };

  const handleError = () => {
    console.error('Login Failed');
    setError('Google Sign-In failed. Please try again.');

    // Track Google Sign-In error
    trackEvent('Authentication', 'Google Sign-In Error', 'User cancelled or error occurred');
  };

  return (
    <div className="page-content" style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '80vh'
    }}>
      <div className="data-table" style={{
        padding: '40px',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center'
      }}>
        <p style={{
          color: '#8b949e',
          fontSize: '14px',
          marginBottom: '30px',
          marginTop: '0'
        }}>
          Welcome to Micropincher! Please sign in with your Google account to continue
        </p>

        {error && (
          <div style={{
            padding: '12px',
            marginBottom: '20px',
            backgroundColor: '#f85149',
            color: '#ffffff',
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            {error}
          </div>
        )}

        {loading && (
          <div style={{
            padding: '12px',
            marginBottom: '20px',
            color: '#8b949e',
            fontSize: '12px'
          }}>
            Logging in...
          </div>
        )}

        {!showOtherOptions ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <GoogleLogin
                onSuccess={handleSuccess}
                onError={handleError}
                useOneTap
                theme="filled_black"
                size="large"
                text="signin_with"
                shape="rectangular"
              />
            </div>

            <div style={{ marginTop: '16px' }}>
              <button
                onClick={() => { setShowOtherOptions(true); setVotedOption(null); }}
                style={{
                  background: 'none', border: '1px solid #30363d', borderRadius: '4px',
                  color: '#8b949e', fontSize: '12px', padding: '6px 14px',
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
                onMouseOver={e => { e.target.style.borderColor = '#8b949e'; e.target.style.color = '#c9d1d9'; }}
                onMouseOut={e => { e.target.style.borderColor = '#30363d'; e.target.style.color = '#8b949e'; }}
              >
                Other option
              </button>
            </div>
          </>
        ) : !votedOption ? (
          <div style={{ marginTop: '24px', textAlign: 'left' }}>
            <p style={{ fontSize: '13px', color: '#c9d1d9', marginBottom: '6px', marginTop: 0 }}>
              Other login options
            </p>
            <p style={{ fontSize: '12px', color: '#8b949e', marginBottom: '16px', marginTop: 0, lineHeight: '1.6' }}>
              Tell us which option you'd prefer — we'll implement the most requested ones first.
              Click on your preferred login method to let us know.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {LOGIN_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => { setVotedOption(opt.label); trackEvent('Login Options', 'Vote', opt.label); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '10px 14px', background: '#0d1117',
                    border: '1px solid #30363d', borderRadius: '6px',
                    color: '#c9d1d9', fontSize: '13px', cursor: 'pointer',
                    transition: 'all 0.2s', textAlign: 'left',
                  }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = '#58a6ff'; e.currentTarget.style.background = '#1a2332'; }}
                  onMouseOut={e => { e.currentTarget.style.borderColor = '#30363d'; e.currentTarget.style.background = '#0d1117'; }}
                >
                  <span style={{ width: '20px', textAlign: 'center', fontSize: '15px', color: '#58a6ff', fontFamily: 'monospace', flexShrink: 0 }}>
                    {opt.icon}
                  </span>
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowOtherOptions(false)}
              style={{
                width: '100%', padding: '8px', background: 'none',
                border: '1px solid #30363d', borderRadius: '6px',
                color: '#8b949e', fontSize: '12px', cursor: 'pointer',
              }}
            >
              Back
            </button>
          </div>
        ) : (
          <div style={{ marginTop: '24px' }}>
            <div style={{ fontSize: '36px', color: '#3fb950', marginBottom: '12px' }}>✓</div>
            <p style={{ fontSize: '14px', color: '#c9d1d9', marginBottom: '8px', marginTop: 0 }}>
              Thank you!
            </p>
            <p style={{ fontSize: '13px', color: '#8b949e', lineHeight: '1.6', marginBottom: '20px', marginTop: 0 }}>
              We noted your interest in <strong style={{ color: '#c9d1d9' }}>{votedOption}</strong>.
              We will implement the most demanded login options as the platform grows.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
              <GoogleLogin
                onSuccess={handleSuccess}
                onError={handleError}
                theme="filled_black"
                size="large"
                text="signin_with"
                shape="rectangular"
              />
            </div>
            <button
              onClick={() => { setShowOtherOptions(false); setVotedOption(null); }}
              style={{
                width: '100%', padding: '8px', background: 'none',
                border: '1px solid #30363d', borderRadius: '6px',
                color: '#8b949e', fontSize: '12px', cursor: 'pointer', marginTop: '8px',
              }}
            >
              Back
            </button>
          </div>
        )}

        <p style={{ marginTop: '20px', fontSize: '11px', color: '#484f58', lineHeight: '1.6' }}>
          By signing in you agree to our{' '}
          <a href="/terms" style={{ color: '#8b949e' }}>Terms &amp; Conditions</a>
          {' '}and{' '}
          <a href="/privacy" style={{ color: '#8b949e' }}>Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
};

export default Login;
